// Chapter 6: MPCI Policy Provisions and Administration
// Comprehensive coverage of policy terms and administrative procedures

const chapter6 = {
  id: 6,
  title: "MPCI Policy Provisions and Administration",
  sections: [
    {
      title: "Policy Structure and Documents",
      content: `
        <h2>Policy Structure and Documents</h2>

        <p>Understanding the structure of an MPCI policy helps agents explain coverage and answer producer questions.</p>

        <div class="key-concept">
          <h3>Key Concept: Policy Components</h3>
          <p>An MPCI policy consists of several documents:</p>
          <ul>
            <li><strong>Basic Provisions:</strong> Common terms that apply to all crop policies</li>
            <li><strong>Crop Provisions:</strong> Specific terms for each insured crop</li>
            <li><strong>Special Provisions:</strong> County-specific modifications</li>
            <li><strong>Actuarial Documents:</strong> Premium rates, dates, and coverage information</li>
            <li><strong>Written Agreements:</strong> Individual modifications (if applicable)</li>
          </ul>
        </div>

        <h3>Basic Provisions</h3>
        <p>The Basic Provisions establish:</p>
        <ul>
          <li>Definitions of key terms</li>
          <li>Eligibility requirements</li>
          <li>Application and reporting requirements</li>
          <li>Premium payment terms</li>
          <li>Duties of the insured</li>
          <li>Claims procedures</li>
          <li>General conditions</li>
        </ul>

        <h3>Crop Provisions</h3>
        <p>Each crop has specific provisions that address:</p>
        <ul>
          <li>Insurable crop definition</li>
          <li>Unit structure options</li>
          <li>Coverage limitations</li>
          <li>Yield calculation methods</li>
          <li>Quality adjustment factors</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the difference between Basic Provisions (apply to all crops) and Crop Provisions (specific to each crop). Special Provisions customize coverage for specific counties.</p>
        </div>
      `
    },
    {
      title: "Eligibility Requirements",
      content: `
        <h2>Eligibility Requirements</h2>

        <p>Not everyone can purchase federal crop insurance. Understanding eligibility requirements is important for agents.</p>

        <h3>Producer Eligibility</h3>
        <ul>
          <li>Must be a citizen of the United States or a resident alien</li>
          <li>Must have an insurable interest in the crop</li>
          <li>Must provide a valid Social Security Number or Tax ID</li>
          <li>Must not be ineligible due to:
            <ul>
              <li>Outstanding debt to FCIC or RMA</li>
              <li>Prior policy termination for cause</li>
              <li>Conviction for crop insurance fraud</li>
              <li>Debarment from federal programs</li>
            </ul>
          </li>
        </ul>

        <div class="important">
          <h3>Important: Ineligibility List</h3>
          <p>RMA maintains an ineligibility list. Producers on this list cannot:</p>
          <ul>
            <li>Obtain new crop insurance policies</li>
            <li>Receive benefits under existing policies</li>
            <li>Receive certain other USDA benefits</li>
          </ul>
          <p>Ineligibility can result from unpaid premiums, fraud, or other violations.</p>
        </div>

        <h3>Crop Eligibility</h3>
        <ul>
          <li>Crop must be insurable in the county</li>
          <li>Must meet crop definition (type, variety, practice)</li>
          <li>Must be grown on eligible land</li>
          <li>Must follow good farming practices</li>
        </ul>

        <h3>Land Eligibility</h3>
        <div class="definition">
          <p>Land may be ineligible if it is:</p>
          <ul>
            <li>Native sod (in some areas, first-time plantings have reduced coverage)</li>
            <li>Land with permanent restrictions on farming</li>
            <li>Land where crop has not been successfully grown</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know that unpaid premiums and fraud can make producers ineligible. Ineligibility affects crop insurance AND other USDA programs.</p>
        </div>
      `
    },
    {
      title: "Premium Billing and Payment",
      content: `
        <h2>Premium Billing and Payment</h2>

        <p>Premium is the cost of insurance. Understanding billing and payment is important for both agents and producers.</p>

        <h3>Premium Due Dates</h3>
        <div class="key-concept">
          <h3>Key Concept: When Premium is Due</h3>
          <p>Premium is typically due:</p>
          <ul>
            <li>After the acreage reporting date</li>
            <li>By the premium billing date specified in actuarial documents</li>
            <li>Before or at harvest (varies by crop)</li>
          </ul>
          <p>Exact dates vary by crop and are listed in the actuarial documents.</p>
        </div>

        <h3>Premium Calculation</h3>
        <p>Total premium depends on:</p>
        <ul>
          <li>Liability (acres × yield × price × coverage level)</li>
          <li>Premium rate (based on county, crop, practice, etc.)</li>
          <li>Unit structure (enterprise units have lower rates)</li>
          <li>Coverage options selected</li>
        </ul>

        <h3>Premium Subsidies</h3>
        <p>The government pays a portion of the premium:</p>
        <table>
          <tr>
            <th>Coverage Level</th>
            <th>Subsidy %</th>
          </tr>
          <tr>
            <td>CAT (50/55)</td>
            <td>100% (admin fee only)</td>
          </tr>
          <tr>
            <td>50-55%</td>
            <td>67%</td>
          </tr>
          <tr>
            <td>60-65%</td>
            <td>64%</td>
          </tr>
          <tr>
            <td>70-75%</td>
            <td>59%</td>
          </tr>
          <tr>
            <td>80-85%</td>
            <td>55%</td>
          </tr>
        </table>

        <div class="important">
          <h3>Important: Failure to Pay Premium</h3>
          <p>If premium is not paid by the due date:</p>
          <ul>
            <li>Policy may be terminated</li>
            <li>Premium debt is still owed</li>
            <li>Producer becomes ineligible for future coverage</li>
            <li>May affect eligibility for other USDA programs</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Premium subsidy percentages decrease at higher coverage levels. Know the consequences of non-payment: termination, debt, and program ineligibility.</p>
        </div>
      `
    },
    {
      title: "Policy Changes and Transfers",
      content: `
        <h2>Policy Changes and Transfers</h2>

        <p>Policies can be changed under certain circumstances. Understanding when and how changes can be made is important.</p>

        <h3>Changes Allowed Before Sales Closing Date</h3>
        <ul>
          <li>Coverage level changes</li>
          <li>Plan of insurance changes</li>
          <li>Unit structure changes</li>
          <li>Adding or removing optional coverages</li>
          <li>Cancelling the policy</li>
        </ul>

        <h3>Changes After Sales Closing Date</h3>
        <div class="key-concept">
          <h3>Key Concept: Limited Changes</h3>
          <p>After the sales closing date, most changes cannot be made. Limited changes may include:</p>
          <ul>
            <li>Corrections to clerical errors</li>
            <li>Changes required by written agreement</li>
            <li>Changes due to land ownership/lease changes</li>
          </ul>
          <p>Coverage level and plan changes must wait until the next sales closing date.</p>
        </div>

        <h3>Policy Transfer</h3>
        <p>When a producer acquires or loses land during the crop year:</p>
        <ul>
          <li><strong>Acquiring Land:</strong> May be able to add acres under certain conditions</li>
          <li><strong>Losing Land:</strong> Coverage may transfer to new operator or be cancelled</li>
          <li><strong>APH Transfer:</strong> Yield history may transfer with proper documentation</li>
        </ul>

        <h3>Change of Entity</h3>
        <div class="definition">
          <p>When the farming entity changes (e.g., from individual to partnership):</p>
          <ul>
            <li>New entity must apply for coverage</li>
            <li>APH may transfer under certain conditions</li>
            <li>Documentation of entity change is required</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Major changes (coverage level, plan) must be made BEFORE the sales closing date. After that, changes are very limited.</p>
        </div>
      `
    },
    {
      title: "Continuous Policy and Cancellation",
      content: `
        <h2>Continuous Policy and Cancellation</h2>

        <p>MPCI policies are continuous, meaning they automatically renew unless cancelled. Understanding this is important.</p>

        <h3>Continuous Coverage</h3>
        <div class="key-concept">
          <h3>Key Concept: Automatic Renewal</h3>
          <p>Once obtained, an MPCI policy:</p>
          <ul>
            <li>Automatically renews each year</li>
            <li>Maintains the same coverage level and options</li>
            <li>Updates with new actuarial information (rates, prices, etc.)</li>
            <li>Requires annual acreage and production reports</li>
          </ul>
          <p>Producers don't need to reapply each year.</p>
        </div>

        <h3>Cancellation by the Insured</h3>
        <ul>
          <li>Must be in writing</li>
          <li>Must be received by the sales closing date</li>
          <li>Effective at the end of the current insurance period</li>
          <li>Cannot cancel mid-season (with limited exceptions)</li>
        </ul>

        <h3>Termination by the Company</h3>
        <p>The insurance company may terminate a policy for:</p>
        <ul>
          <li>Non-payment of premium</li>
          <li>Outstanding debt to FCIC</li>
          <li>Material misrepresentation on application</li>
          <li>Fraud</li>
          <li>Loss of eligibility</li>
        </ul>

        <h3>Void Policies</h3>
        <div class="important">
          <h3>Important: When a Policy is Void</h3>
          <p>A policy is void (as if it never existed) if:</p>
          <ul>
            <li>The insured was never eligible for coverage</li>
            <li>Material misrepresentation was made at application</li>
            <li>Fraud is discovered</li>
          </ul>
          <p>When voided, all indemnities must be repaid and premium may not be refunded.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Policies are continuous - they auto-renew. Cancellation must be done by the sales closing date. Void policies are treated as if they never existed.</p>
        </div>
      `
    },
    {
      title: "Share Arrangements",
      content: `
        <h2>Share Arrangements</h2>

        <p>Many producers share crop production with landlords or other parties. Understanding how shares affect coverage is essential.</p>

        <h3>Insurable Interest by Share</h3>
        <div class="key-concept">
          <h3>Key Concept: Share Determines Coverage</h3>
          <p>Each party can only insure their own share of the crop:</p>
          <ul>
            <li>A tenant with 60% share can insure 60%</li>
            <li>A landlord with 40% share can insure 40%</li>
            <li>Each party needs their own policy</li>
            <li>Combined coverage cannot exceed 100%</li>
          </ul>
        </div>

        <h3>Types of Share Arrangements</h3>

        <div class="definition">
          <h4>Share Rent</h4>
          <p>Landlord receives a percentage of the crop as rent. Both parties share in production risk.</p>
        </div>

        <div class="definition">
          <h4>Cash Rent</h4>
          <p>Tenant pays a fixed amount of cash rent. Tenant bears all production risk and has 100% share.</p>
        </div>

        <div class="definition">
          <h4>Flex Rent</h4>
          <p>Rent varies based on crop prices or production. Share determination follows specific rules.</p>
        </div>

        <div class="example">
          <h4>Example: Share Rent Coverage</h4>
          <p>A 60/40 share rent arrangement:</p>
          <ul>
            <li>Tenant has 60% insurable share</li>
            <li>Landlord has 40% insurable share</li>
            <li>Tenant's guarantee = Total guarantee × 60%</li>
            <li>Landlord's guarantee = Total guarantee × 40%</li>
          </ul>
          <p>Each party's indemnity is based on their share only.</p>
        </div>

        <h3>Reporting Shares</h3>
        <ul>
          <li>Shares must be reported on acreage report</li>
          <li>All parties with shares should be identified</li>
          <li>Share percentages must total 100%</li>
          <li>Changes in share must be reported</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Each party insures only their share. In cash rent, tenant has 100% share. In share rent, shares are split between tenant and landlord.</p>
        </div>
      `
    },
    {
      title: "Spouses and Substantial Beneficial Interest",
      content: `
        <h2>Spouses and Substantial Beneficial Interest</h2>

        <p>Rules regarding spouses and related entities are important for determining eligibility and preventing abuse.</p>

        <h3>Substantial Beneficial Interest (SBI)</h3>
        <div class="definition">
          <p><strong>Substantial Beneficial Interest</strong> means ownership of 10% or more of the entity. Anyone with SBI is subject to the same eligibility rules as the primary insured.</p>
        </div>

        <h3>SBI Reporting Requirements</h3>
        <ul>
          <li>All persons with SBI must be listed</li>
          <li>SSN or Tax ID required for each</li>
          <li>Ineligibility of any SBI holder affects the policy</li>
          <li>Must be updated when ownership changes</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: SBI Impact on Eligibility</h3>
          <p>If any person with SBI is:</p>
          <ul>
            <li><strong>Ineligible:</strong> The entity may be ineligible</li>
            <li><strong>Indebted to FCIC:</strong> Entity may be ineligible until debt is paid</li>
            <li><strong>Disqualified for fraud:</strong> Entity may be disqualified</li>
          </ul>
        </div>

        <h3>Spouse Provisions</h3>
        <ul>
          <li>Spouses may insure separately if they have separate farming operations</li>
          <li>Combined share cannot exceed 100% of the crop</li>
          <li>Ineligibility of one spouse may affect the other</li>
          <li>Premium debt of one spouse may affect the other</li>
        </ul>

        <div class="example">
          <h4>Example: SBI Scenario</h4>
          <p>A farming partnership has three partners:</p>
          <ul>
            <li>Partner A: 50% ownership</li>
            <li>Partner B: 30% ownership</li>
            <li>Partner C: 20% ownership</li>
          </ul>
          <p>All three have SBI (>10%). If Partner C has unpaid premium debt, the entire partnership may be ineligible for coverage.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>SBI is 10% or more ownership. Ineligibility of anyone with SBI can affect the entire entity's coverage.</p>
        </div>
      `
    },
    {
      title: "Agent Responsibilities",
      content: `
        <h2>Agent Responsibilities</h2>

        <p>Crop insurance agents have specific duties and responsibilities. Understanding these is essential for professional practice.</p>

        <h3>Core Agent Duties</h3>
        <ul>
          <li><strong>Explain Coverage:</strong> Help producers understand their options</li>
          <li><strong>Complete Applications:</strong> Accurately record producer information</li>
          <li><strong>Submit Reports:</strong> File acreage and production reports timely</li>
          <li><strong>Maintain Records:</strong> Keep documentation for required periods</li>
          <li><strong>Report Fraud:</strong> Notify company of suspected fraud</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Agent as Company Representative</h3>
          <p>When acting for the insurance company, the agent:</p>
          <ul>
            <li>Represents the company, not the producer</li>
            <li>Must follow company and RMA procedures</li>
            <li>Can bind the company to coverage within authority</li>
            <li>Is responsible for accuracy of submitted documents</li>
          </ul>
        </div>

        <h3>Prohibited Activities</h3>
        <ul>
          <li>Making false statements on applications</li>
          <li>Encouraging or assisting in fraud</li>
          <li>Rebating premiums</li>
          <li>Misrepresenting coverage</li>
          <li>Signing documents on behalf of producers without authorization</li>
        </ul>

        <h3>Continuing Education</h3>
        <ul>
          <li>Agents must complete required training</li>
          <li>Updates on program changes</li>
          <li>Certification requirements vary by company and state</li>
        </ul>

        <div class="important">
          <h3>Important: Agent Liability</h3>
          <p>Agents can be held liable for:</p>
          <ul>
            <li>Errors that result in coverage gaps</li>
            <li>Failure to submit reports timely</li>
            <li>Participating in or ignoring fraud</li>
            <li>Misrepresenting coverage to producers</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Agents represent the company, not the producer. They must report suspected fraud and cannot assist in fraudulent activities.</p>
        </div>
      `
    },
    {
      title: "Record Keeping Requirements",
      content: `
        <h2>Record Keeping Requirements</h2>

        <p>Proper record keeping is essential for both producers and agents. Records support claims and verify compliance.</p>

        <h3>Producer Record Requirements</h3>
        <div class="key-concept">
          <h3>Key Concept: Required Records</h3>
          <p>Producers must maintain:</p>
          <ul>
            <li>Production records by crop and unit</li>
            <li>Sales receipts and settlement sheets</li>
            <li>Acreage records</li>
            <li>Planting and harvest dates</li>
            <li>Input records (seed, fertilizer, etc.)</li>
          </ul>
          <p>Records must be kept for at least 3 years after the crop year.</p>
        </div>

        <h3>Agent Record Requirements</h3>
        <ul>
          <li>Copies of applications and reports</li>
          <li>Client communication records</li>
          <li>Training and certification documents</li>
          <li>Records of policy changes</li>
        </ul>

        <h3>Audit and Inspection</h3>
        <div class="important">
          <h3>Important: Right to Audit</h3>
          <p>The insurance company and RMA have the right to:</p>
          <ul>
            <li>Audit producer records at any time</li>
            <li>Inspect fields and storage facilities</li>
            <li>Verify production claims</li>
            <li>Review agent files</li>
          </ul>
          <p>Failure to provide records can result in claim denial or policy voidance.</p>
        </div>

        <h3>Electronic Records</h3>
        <ul>
          <li>Electronic records are generally acceptable</li>
          <li>Must be able to produce printed copies</li>
          <li>Must be stored securely</li>
          <li>Must be readily accessible</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Records must be kept for at least 3 years. RMA and the insurance company can audit at any time. Failure to provide records can void coverage.</p>
        </div>
      `
    },
    {
      title: "Sanctions and Penalties",
      content: `
        <h2>Sanctions and Penalties</h2>

        <p>Violations of crop insurance rules can result in serious sanctions. Understanding the consequences helps prevent problems.</p>

        <h3>Types of Sanctions</h3>

        <div class="definition">
          <h4>Administrative Sanctions</h4>
          <ul>
            <li>Claim denial</li>
            <li>Premium debt collection</li>
            <li>Policy termination</li>
            <li>Program ineligibility</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Civil Sanctions</h4>
          <ul>
            <li>Recovery of overpayments</li>
            <li>Civil money penalties</li>
            <li>Interest on amounts owed</li>
            <li>Treble damages in some cases</li>
          </ul>
        </div>

        <div class="definition">
          <h4>Criminal Sanctions</h4>
          <ul>
            <li>Fines up to $10,000 per violation</li>
            <li>Imprisonment up to 5 years</li>
            <li>Both for serious fraud</li>
          </ul>
        </div>

        <h3>Common Violations</h3>
        <ul>
          <li>False statements on applications or claims</li>
          <li>Hiding production</li>
          <li>Misreporting acreage</li>
          <li>Manipulating APH yields</li>
          <li>Claiming uninsured causes of loss</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Program Disqualification</h3>
          <p>Producers found guilty of fraud may be:</p>
          <ul>
            <li>Disqualified from crop insurance for 2-10 years</li>
            <li>Required to repay all benefits received</li>
            <li>Subject to civil penalties</li>
            <li>Ineligible for other USDA programs</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the types of sanctions: administrative, civil, and criminal. Fraud can result in imprisonment and long-term program disqualification.</p>
        </div>
      `
    },
    {
      title: "Privacy and Confidentiality",
      content: `
        <h2>Privacy and Confidentiality</h2>

        <p>Crop insurance involves sensitive personal and business information. Privacy rules protect this information.</p>

        <h3>Protected Information</h3>
        <ul>
          <li>Producer identification (SSN, Tax ID)</li>
          <li>Production and yield data</li>
          <li>Financial information</li>
          <li>Claims history</li>
          <li>Acreage and location information</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Information Sharing</h3>
          <p>Producer information may be shared with:</p>
          <ul>
            <li>The insurance company (AIP)</li>
            <li>RMA and FCIC</li>
            <li>FSA (Farm Service Agency)</li>
            <li>Authorized adjusters</li>
            <li>Other authorized government agencies</li>
          </ul>
          <p>Information is NOT shared with the general public without authorization.</p>
        </div>

        <h3>Agent Confidentiality</h3>
        <ul>
          <li>Agents must protect client information</li>
          <li>Cannot share with unauthorized parties</li>
          <li>Must secure records properly</li>
          <li>May face penalties for unauthorized disclosure</li>
        </ul>

        <h3>FOIA Considerations</h3>
        <div class="definition">
          <p>While crop insurance is a federal program, producer-specific information is generally exempt from Freedom of Information Act (FOIA) disclosure to protect privacy.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Producer information is shared with authorized parties (AIP, RMA, FSA) but is protected from general public disclosure.</p>
        </div>
      `
    },
    {
      title: "Good Farming Practices",
      content: `
        <h2>Good Farming Practices</h2>

        <p>Good Farming Practices (GFP) are required for coverage. Understanding what constitutes GFP is essential.</p>

        <div class="definition">
          <h4>What Are Good Farming Practices?</h4>
          <p>GFP are production methods that are:</p>
          <ul>
            <li>Recognized by agricultural experts in the area</li>
            <li>Appropriate for the crop and region</li>
            <li>Carried out in a timely manner</li>
            <li>Designed to produce a normal yield</li>
          </ul>
        </div>

        <h3>Examples of Good Farming Practices</h3>
        <ul>
          <li><strong>Planting:</strong> Proper dates, rates, and methods</li>
          <li><strong>Pest Control:</strong> Appropriate weed, insect, and disease management</li>
          <li><strong>Fertilization:</strong> Adequate soil fertility management</li>
          <li><strong>Irrigation:</strong> Proper water management where applicable</li>
          <li><strong>Harvest:</strong> Timely and appropriate harvest methods</li>
          <li><strong>Post-Damage:</strong> Protecting crop after damage occurs</li>
        </ul>

        <div class="important">
          <h3>Important: GFP Violations</h3>
          <p>If GFP are not followed:</p>
          <ul>
            <li>Claims may be denied</li>
            <li>Production may be assigned rather than actual</li>
            <li>Coverage may be reduced or voided</li>
          </ul>
          <p>The burden is on the producer to demonstrate they followed GFP.</p>
        </div>

        <h3>Organic Farming Practices</h3>
        <div class="key-concept">
          <h3>Key Concept: Organic GFP</h3>
          <p>For organic crops:</p>
          <ul>
            <li>Must follow certified organic practices</li>
            <li>Organic methods are considered GFP if properly certified</li>
            <li>Lower yields due to organic methods are not considered poor practice</li>
            <li>Must maintain organic certification</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>GFP are required for coverage. The producer must show they followed practices recognized by experts for the area. Organic practices are GFP if certified.</p>
        </div>
      `
    },
    {
      title: "Special Situations and Exceptions",
      content: `
        <h2>Special Situations and Exceptions</h2>

        <p>Crop insurance has provisions for various special situations that don't fit standard rules.</p>

        <h3>Written Agreements</h3>
        <div class="definition">
          <p>Written agreements allow coverage or terms not available under standard provisions:</p>
          <ul>
            <li>Coverage in non-insurable areas</li>
            <li>Yields for new crops or practices</li>
            <li>Modified dates or terms</li>
            <li>Special organic provisions</li>
          </ul>
          <p>Must be requested before the sales closing date and approved by RMA.</p>
        </div>

        <h3>Beginning Farmers</h3>
        <div class="key-concept">
          <h3>Key Concept: Beginning Farmer Benefits</h3>
          <p>Beginning farmers may receive:</p>
          <ul>
            <li>Reduced premium (10% additional subsidy)</li>
            <li>Waived administrative fees for CAT</li>
            <li>Higher T-yields in some cases</li>
          </ul>
          <p>Must meet specific criteria for "beginning farmer" status.</p>
        </div>

        <h3>Specialty Crops</h3>
        <ul>
          <li>May have limited coverage options</li>
          <li>May require written agreements</li>
          <li>Often covered under Whole-Farm Revenue Protection</li>
          <li>Some have crop-specific policies</li>
        </ul>

        <h3>Disaster Situations</h3>
        <ul>
          <li>RMA may extend deadlines after disasters</li>
          <li>Special loss adjustment procedures may apply</li>
          <li>Prevented planting provisions activate</li>
          <li>Emergency provisions may be issued</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Written agreements handle special situations. Beginning farmers get premium discounts. RMA can extend deadlines after disasters.</p>
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "An MPCI policy consists of all the following EXCEPT:",
      options: [
        "Basic Provisions",
        "Crop Provisions",
        "Agent Guidelines",
        "Special Provisions"
      ],
      correct: 2,
      explanation: "MPCI policies consist of Basic Provisions, Crop Provisions, Special Provisions, and Actuarial Documents. Agent guidelines are not part of the policy document."
    },
    {
      question: "A producer becomes ineligible for crop insurance if they:",
      options: [
        "Have lower than average yields",
        "Have outstanding debt to FCIC",
        "Switch insurance agents",
        "Grow multiple crops"
      ],
      correct: 1,
      explanation: "Outstanding debt to FCIC or RMA makes a producer ineligible for coverage. The other options do not affect eligibility."
    },
    {
      question: "Premium subsidies are highest at which coverage level?",
      options: [
        "50% coverage",
        "70% coverage",
        "85% coverage",
        "Premium subsidies are the same at all levels"
      ],
      correct: 0,
      explanation: "Premium subsidy percentages are highest at lower coverage levels (67% at 50%) and decrease as coverage increases (55% at 85%)."
    },
    {
      question: "Changes to coverage level must be made:",
      options: [
        "By the acreage reporting date",
        "By the sales closing date",
        "Any time during the year",
        "After harvest"
      ],
      correct: 1,
      explanation: "Coverage level changes must be made by the sales closing date. After that, changes are very limited and coverage level cannot be modified."
    },
    {
      question: "MPCI policies are described as 'continuous' which means:",
      options: [
        "Coverage never lapses",
        "They automatically renew each year",
        "They cover the entire growing season",
        "They can be changed at any time"
      ],
      correct: 1,
      explanation: "Continuous policies automatically renew for the next crop year unless cancelled by the sales closing date. Producers don't need to reapply annually."
    },
    {
      question: "In a 70/30 share rent arrangement, the landlord can insure:",
      options: [
        "100% of the crop",
        "70% of the crop",
        "30% of the crop",
        "50% of the crop"
      ],
      correct: 2,
      explanation: "Each party can only insure their share. In a 70/30 arrangement, the landlord's share is 30%, so they can only insure 30% of the crop."
    },
    {
      question: "Substantial Beneficial Interest (SBI) means ownership of at least:",
      options: [
        "5%",
        "10%",
        "25%",
        "51%"
      ],
      correct: 1,
      explanation: "SBI is defined as 10% or more ownership in an entity. Anyone with SBI is subject to the same eligibility rules as the primary insured."
    },
    {
      question: "If a person with SBI is ineligible for crop insurance:",
      options: [
        "They alone are affected",
        "The entire entity may be ineligible",
        "Only their share is uninsurable",
        "Nothing happens until they have 51% ownership"
      ],
      correct: 1,
      explanation: "Ineligibility of any person with SBI (10%+ ownership) can affect the entire entity's eligibility for coverage."
    },
    {
      question: "Crop insurance agents represent:",
      options: [
        "The producer only",
        "The insurance company",
        "Both equally",
        "Neither - they are independent"
      ],
      correct: 1,
      explanation: "When acting in their official capacity, agents represent the insurance company, not the producer. They must follow company and RMA procedures."
    },
    {
      question: "Production records must be maintained for at least:",
      options: [
        "1 year",
        "3 years",
        "5 years",
        "7 years"
      ],
      correct: 1,
      explanation: "Producers must maintain production records for at least 3 years after the crop year. These records may be audited at any time."
    },
    {
      question: "Criminal sanctions for crop insurance fraud can include:",
      options: [
        "Only fines",
        "Only imprisonment",
        "Both fines and imprisonment",
        "Neither - fraud is a civil matter only"
      ],
      correct: 2,
      explanation: "Criminal sanctions can include fines up to $10,000 per violation AND imprisonment up to 5 years. Fraud is taken very seriously."
    },
    {
      question: "A policy may be declared 'void' if:",
      options: [
        "The producer has a small loss",
        "Premium is paid late",
        "Material misrepresentation was made at application",
        "The producer changes agents"
      ],
      correct: 2,
      explanation: "A policy is void (as if it never existed) if the insured was never eligible, made material misrepresentations, or committed fraud."
    },
    {
      question: "In a cash rent arrangement, the tenant has what share?",
      options: [
        "0%",
        "50%",
        "75%",
        "100%"
      ],
      correct: 3,
      explanation: "In cash rent, the tenant pays a fixed rent and bears all production risk. Therefore, the tenant has 100% of the insurable share."
    },
    {
      question: "Good Farming Practices (GFP) are defined as:",
      options: [
        "Any practices the producer chooses",
        "Production methods recognized by agricultural experts in the area",
        "Methods that maximize yield regardless of cost",
        "Organic farming only"
      ],
      correct: 1,
      explanation: "GFP are production methods recognized by agricultural experts in the area as appropriate for the crop and region, carried out in a timely manner."
    },
    {
      question: "If GFP are not followed:",
      options: [
        "Premium is increased",
        "Claims may be denied",
        "Coverage automatically increases",
        "Nothing happens"
      ],
      correct: 1,
      explanation: "Failure to follow GFP can result in claim denial, assigned yields instead of actual, and reduced or voided coverage."
    },
    {
      question: "Beginning farmers may receive:",
      options: [
        "Free coverage for 5 years",
        "Reduced premium through additional subsidy",
        "No coverage requirements",
        "Guaranteed indemnity payments"
      ],
      correct: 1,
      explanation: "Beginning farmers may receive additional premium subsidy (10% more), waived CAT administrative fees, and higher T-yields in some cases."
    },
    {
      question: "Producer information may be shared with:",
      options: [
        "The general public",
        "Only the producer",
        "Authorized government agencies like RMA and FSA",
        "Anyone who requests it"
      ],
      correct: 2,
      explanation: "Producer information may be shared with authorized parties like the AIP, RMA, FSA, and authorized adjusters. It is protected from general public disclosure."
    },
    {
      question: "Written agreements are used for:",
      options: [
        "All crop insurance policies",
        "Situations not covered by standard provisions",
        "Reducing premiums",
        "Avoiding claims procedures"
      ],
      correct: 1,
      explanation: "Written agreements allow coverage or terms not available under standard provisions, such as coverage in non-insurable areas or special crop situations."
    },
    {
      question: "After a disaster, RMA may:",
      options: [
        "Automatically cancel all policies",
        "Extend reporting deadlines",
        "Eliminate all coverage requirements",
        "Reduce premium subsidies"
      ],
      correct: 1,
      explanation: "After disasters, RMA may extend deadlines and issue special provisions to help producers. Policies are not automatically cancelled."
    },
    {
      question: "The right to audit producer records belongs to:",
      options: [
        "Only the insurance company",
        "Only RMA",
        "Both the insurance company and RMA",
        "Neither - producers have complete privacy"
      ],
      correct: 2,
      explanation: "Both the insurance company (AIP) and RMA have the right to audit producer records at any time to verify compliance and claims."
    },
    {
      question: "Agents can be held liable for:",
      options: [
        "Low crop yields",
        "Weather-related losses",
        "Errors that result in coverage gaps",
        "RMA policy changes"
      ],
      correct: 2,
      explanation: "Agents can be liable for errors that result in coverage gaps, failure to submit reports timely, participating in fraud, or misrepresenting coverage."
    },
    {
      question: "Premium is typically due:",
      options: [
        "Before the sales closing date",
        "At the time of application",
        "After the acreage reporting date",
        "At harvest only"
      ],
      correct: 2,
      explanation: "Premium is typically due after the acreage reporting date, by the premium billing date specified in the actuarial documents. The exact date varies by crop."
    },
    {
      question: "Which statement about policy termination is TRUE?",
      options: [
        "Producers can cancel anytime",
        "Premium debt is forgiven upon termination",
        "Premium debt is still owed even after termination",
        "Terminated producers remain eligible for future coverage"
      ],
      correct: 2,
      explanation: "Even if a policy is terminated, premium debt is still owed. The producer becomes ineligible for future coverage until the debt is paid."
    },
    {
      question: "Organic farming practices are considered GFP if:",
      options: [
        "Never - organic is not allowed",
        "The operation is certified organic",
        "Yields match conventional farming",
        "The producer requests it"
      ],
      correct: 1,
      explanation: "For certified organic operations, organic practices are considered GFP. Lower yields due to organic methods are not considered poor practice."
    },
    {
      question: "Program disqualification for fraud may last:",
      options: [
        "1 year only",
        "2-10 years",
        "Permanently in all cases",
        "6 months"
      ],
      correct: 1,
      explanation: "Producers found guilty of fraud may be disqualified from crop insurance for 2-10 years, depending on the severity of the violation."
    },
    {
      question: "A transfer of coverage to a new producer requires:",
      options: [
        "Nothing - coverage transfers automatically",
        "Written request and AIP approval before the acreage reporting date",
        "Payment of an additional premium",
        "A new sales closing date"
      ],
      correct: 1,
      explanation: "Transferring coverage to a new producer requires a written request and AIP approval, typically before the acreage reporting date. Coverage does not transfer automatically."
    },
    {
      question: "The actuarial documents for a crop specify:",
      options: [
        "Only the premium rates",
        "Dates, rates, coverage levels, and other crop-specific provisions",
        "Only the final planting dates",
        "Agent commission rates"
      ],
      correct: 1,
      explanation: "Actuarial documents specify important dates (SCD, ARD, final planting), premium rates, available coverage levels, T-yields, and other crop-specific information."
    },
    {
      question: "What is the consequence of late premium payment?",
      options: [
        "Coverage is enhanced",
        "Interest is charged and coverage may be terminated",
        "No consequence if paid before harvest",
        "The deductible is waived"
      ],
      correct: 1,
      explanation: "Late premium payment results in interest charges and may lead to policy termination and loss of coverage if premium remains unpaid."
    },
    {
      question: "A spouse's ineligibility for crop insurance:",
      options: [
        "Has no effect on the other spouse",
        "May affect the other spouse's eligibility",
        "Only matters for joint policies",
        "Is automatically waived after 1 year"
      ],
      correct: 1,
      explanation: "A spouse's ineligibility (such as from program violations or debt) may affect the other spouse's eligibility, especially when they share farming operations or have joint interests."
    },
    {
      question: "The Basic Provisions of an MPCI policy contain:",
      options: [
        "Crop-specific planting dates",
        "General terms applicable to all crops",
        "County premium rates",
        "Agent commission schedules"
      ],
      correct: 1,
      explanation: "Basic Provisions contain general terms that apply to all crops, including definitions, general conditions, duties of the insured, and claim procedures."
    }
  ]
};

export default chapter6;
